import { AppError } from './AppError';
import { ERROR_CODES, USER_MESSAGE_KEYS } from './errorCodes';
import { describeErrorSource } from './describeError';
import { TimeoutError } from '../utils/timeout';
import type { NormalizeContext } from './types';

function extractFirebaseCode(error: unknown): string {
  const code = (error as { code?: string })?.code;
  if (typeof code !== 'string') return '';
  if (code.startsWith('functions/')) return code.slice('functions/'.length);
  return code;
}

function extractDetails(error: unknown): Record<string, unknown> {
  const details = (error as { details?: unknown })?.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

function withSourceDetails(
  details: Record<string, unknown>,
  error: unknown,
): Record<string, unknown> {
  const source = describeErrorSource(error);
  return {
    ...details,
    ...source,
    causeMessage: source.sourceMessage || details.causeMessage,
  };
}

function withTrace(base: AppError, ctx?: NormalizeContext): AppError {
  if (!ctx?.traceId && !ctx?.spanId && !ctx?.operation) return base;
  return new AppError({
    code: base.code,
    category: base.category,
    userMessageKey: base.userMessageKey,
    hintKey: base.hintKey,
    severity: base.severity,
    retryable: base.retryable,
    presentation: base.presentation,
    details: { ...base.details, operation: ctx.operation },
    traceId: ctx.traceId ?? base.traceId,
    spanId: ctx.spanId ?? base.spanId,
  }, base.message);
}

function authClientError(
  firebaseCode: string,
  details: Record<string, unknown>,
  userMessageKey = USER_MESSAGE_KEYS.GENERIC,
  hintKey = USER_MESSAGE_KEYS.GENERIC_HINT,
): AppError {
  return new AppError({
    code: ERROR_CODES.AUTH_REQUIRED,
    category: 'auth',
    userMessageKey,
    hintKey,
    presentation: 'inline',
    severity: 'warn',
    details: { firebaseCode, ...details },
  }, firebaseCode);
}

function fromFirebaseCode(
  firebaseCode: string,
  details: Record<string, unknown>,
  sourceMessage: string,
): AppError {
  const detailCode = typeof details.code === 'string' ? details.code : undefined;
  const detailKey = typeof details.userMessageKey === 'string' ? details.userMessageKey : undefined;
  const billingKey = sourceMessage.startsWith('billing/')
    ? sourceMessage.split(':')[0]
    : undefined;
  const enriched = { ...details, firebaseCode, sourceMessage };

  if (detailKey) {
    return AppError.validation(detailKey, enriched);
  }

  if (firebaseCode === 'auth/requires-recent-login') {
    return new AppError({
      code: ERROR_CODES.AUTH_SESSION_EXPIRED,
      category: 'auth',
      userMessageKey: USER_MESSAGE_KEYS.MFA_REQUIRES_RECENT_LOGIN,
      presentation: 'inline',
      severity: 'warn',
      details: enriched,
    }, firebaseCode);
  }

  if (
    firebaseCode === 'unauthenticated'
    || firebaseCode === 'auth/user-token-expired'
    || firebaseCode === 'auth/invalid-user-token'
  ) {
    return new AppError({
      code: ERROR_CODES.AUTH_REQUIRED,
      category: 'auth',
      userMessageKey: USER_MESSAGE_KEYS.AUTH_REQUIRED,
      hintKey: USER_MESSAGE_KEYS.AUTH_REQUIRED_HINT,
      details: enriched,
    }, firebaseCode);
  }

  // Expected client-side Auth outcomes — warn/inline, not internal.unexpected.
  if (
    firebaseCode === 'auth/popup-closed-by-user'
    || firebaseCode === 'auth/cancelled-popup-request'
    || firebaseCode === 'auth/popup-blocked'
    || firebaseCode === 'auth/user-cancelled'
    || firebaseCode === 'auth/unauthorized-domain'
    || firebaseCode === 'auth/operation-not-allowed'
    || firebaseCode === 'auth/credential-already-in-use'
    || firebaseCode === 'auth/email-already-in-use'
    || firebaseCode === 'auth/account-exists-with-different-credential'
    || firebaseCode === 'auth/invalid-credential'
    || firebaseCode === 'auth/wrong-password'
    || firebaseCode === 'auth/user-not-found'
    || firebaseCode === 'auth/invalid-email'
    || firebaseCode === 'auth/too-many-requests'
    || firebaseCode === 'auth/network-request-failed'
    || firebaseCode === 'auth/google-email-mismatch'
    || firebaseCode.startsWith('auth/')
  ) {
    return authClientError(firebaseCode, enriched);
  }

  switch (firebaseCode) {
    case 'permission-denied':
      return AppError.permissionDenied(enriched);
    case 'not-found':
      return new AppError({
        code: ERROR_CODES.NOT_FOUND,
        category: 'not_found',
        userMessageKey: USER_MESSAGE_KEYS.NOT_FOUND,
        details: enriched,
      }, firebaseCode);
    case 'already-exists':
    case 'aborted':
      return new AppError({
        code: ERROR_CODES.CONFLICT,
        category: 'conflict',
        userMessageKey: USER_MESSAGE_KEYS.CONFLICT,
        details: enriched,
      }, firebaseCode);
    case 'resource-exhausted':
      return new AppError({
        code: ERROR_CODES.RATE_LIMIT,
        category: 'rate_limit',
        userMessageKey: USER_MESSAGE_KEYS.RATE_LIMIT,
        retryable: true,
        details: enriched,
      }, firebaseCode);
    case 'unavailable':
    case 'deadline-exceeded':
      return AppError.unavailable(enriched);
    case 'invalid-argument':
    case 'failed-precondition':
      return new AppError({
        code: detailCode ?? ERROR_CODES.VALIDATION,
        category: 'validation',
        userMessageKey: detailKey ?? billingKey ?? USER_MESSAGE_KEYS.PLAY_FAILED,
        hintKey: USER_MESSAGE_KEYS.PLAY_FAILED_HINT,
        details: enriched,
      }, sourceMessage || firebaseCode);
    case 'internal':
      return AppError.internal(sourceMessage || firebaseCode, enriched);
    default:
      return new AppError({
        code: ERROR_CODES.INTERNAL,
        category: 'internal',
        userMessageKey: USER_MESSAGE_KEYS.GENERIC,
        hintKey: USER_MESSAGE_KEYS.GENERIC_HINT,
        details: enriched,
      }, sourceMessage || firebaseCode || ERROR_CODES.INTERNAL);
  }
}

function fromMessageHeuristics(message: string, details: Record<string, unknown>): AppError | null {
  const lower = message.toLowerCase();
  if (/internal assertion/i.test(message)) {
    return new AppError({
      code: ERROR_CODES.INTERNAL,
      category: 'internal',
      userMessageKey: USER_MESSAGE_KEYS.GENERIC,
      presentation: 'silent',
      severity: 'info',
      details: { ...details, noise: 'firestore-internal-assertion' },
    }, message);
  }
  if (
    /offline|failed to fetch|network error|err_internet_disconnected|networkerror|chunkloaderror|importing a module script failed|load failed/i
      .test(lower)
  ) {
    return AppError.offline(details);
  }
  if (/permission|denied|not authorized|unauthorized/i.test(lower)) {
    return AppError.permissionDenied(details);
  }
  if (/unavailable|timeout|deadline/i.test(lower)) {
    return AppError.unavailable(details);
  }
  if (/select a house/i.test(lower)) {
    return AppError.permissionDenied({ reason: 'house_scope_required', ...details });
  }
  return null;
}

/**
 * Convert any thrown value into an AppError with stable code and user-safe i18n key.
 */
export function normalizeError(error: unknown, ctx?: NormalizeContext): AppError {
  if (isAppErrorPassthrough(error)) return withTrace(error, ctx);

  if (typeof error === 'string') {
    if (error.startsWith('error_')) return withTrace(AppError.fromValidationKey(error), ctx);
    return withTrace(AppError.internal(error, describeErrorSource(error)), ctx);
  }

  const source = describeErrorSource(error);
  const details = withSourceDetails(extractDetails(error), error);
  const firebaseCode = extractFirebaseCode(error);
  if (firebaseCode) {
    return withTrace(fromFirebaseCode(firebaseCode, details, source.sourceMessage), ctx);
  }

  if (error instanceof TimeoutError) {
    return withTrace(new AppError({
      code: ERROR_CODES.NETWORK_UNAVAILABLE,
      category: 'unavailable',
      userMessageKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE,
      hintKey: USER_MESSAGE_KEYS.SERVER_UNREACHABLE_HINT,
      retryable: false,
      details,
    }, error.message), ctx);
  }

  const heuristic = fromMessageHeuristics(source.sourceMessage, details);
  if (heuristic) return withTrace(heuristic, ctx);

  return withTrace(
    AppError.internal(error, details),
    ctx,
  );
}

function isAppErrorPassthrough(error: unknown): error is AppError {
  return error instanceof AppError;
}

/** @deprecated alias */
export const normalizeUnknownError = normalizeError;
